"use client";

import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import { useTranslations } from 'next-intl';
import Link from 'next/link';
import {
  useRouter,
  useSearchParams,
} from 'next/navigation';
import toast from 'react-hot-toast';

import config from '@/config';
import type { SeoAttributionInput } from '@/content/seo/types';
import CloudflareTurnstile from '@/components/CloudflareTurnstile';
import {
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/16/solid';
import { trackSeoEvent } from '@/utils/seoAnalytics';
import {
  normalizeSeoAttribution,
} from '@/utils/seoAttribution';
import { getSafeAuthReturnPath } from '@/utils/auth-return-path';
import {
  appendFacelessWorkflowPresetToPath,
  facelessWorkflowPresetFromSearchParams,
} from '@/utils/facelessWorkflowPreset';
import { getLocalizedPath } from '@/utils/utils';
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs';

export const runtime = 'edge';

interface RegisterPageProps {
  params: { locale: string };
}

type TurnstileStatus = 'ready' | 'verified' | 'expired' | 'error' | 'timeout' | 'unsupported';

const getTurnstileStatusMessage = (status: TurnstileStatus, errorCode: string | undefined, locale: string) => {
  const isChineseLocale = locale.startsWith("zh");

  if (status === "expired") {
    return isChineseLocale ? "Cloudflare 验证已过期，请重新完成验证。" : "Cloudflare verification expired. Please complete it again.";
  }

  if (status === "timeout") {
    return isChineseLocale ? "Cloudflare 验证超时，请重新完成验证。" : "Cloudflare verification timed out. Please complete it again.";
  }

  if (status === "unsupported") {
    return isChineseLocale
      ? "当前浏览器不支持 Cloudflare 验证，请换用其他浏览器或关闭相关插件后重试。"
      : "This browser does not support Cloudflare verification. Try another browser or disable blocking extensions.";
  }

  if (status === "error") {
    return isChineseLocale
      ? `Cloudflare 验证失败，请刷新验证后重试${errorCode ? `（错误码：${errorCode}）` : ""}。`
      : `Cloudflare verification failed. Refresh the challenge and try again${errorCode ? ` (code: ${errorCode})` : ""}.`;
  }

  return null;
};

const getSignupErrorMessage = (message: string | undefined, code: string | undefined, locale: string) => {
  const normalizedMessage = (message || "").toLowerCase();
  const normalizedCode = (code || "").toLowerCase();
  const isChineseLocale = locale.startsWith("zh");

  if (normalizedCode === "captcha_failed" || normalizedMessage.includes("captcha") || normalizedMessage.includes("challenge")) {
    return isChineseLocale
      ? "Cloudflare 验证未通过，请重新完成验证后再继续。"
      : "Cloudflare verification failed. Please complete the challenge again before continuing.";
  }

  if (normalizedMessage.includes("already registered") || normalizedMessage.includes("already been registered")) {
    return isChineseLocale
      ? "该邮箱已注册，请直接登录；如果你用 Google 注册，请使用 Google 登录。"
      : "This email is already registered. Sign in instead, or use Google sign in if you signed up with Google.";
  }

  return message || (isChineseLocale ? "注册失败，请稍后重试。" : "Signup failed. Please try again.");
};

export default function Register({ params: { locale } }: RegisterPageProps) {
  const t = useTranslations('Auth');
  const supabase = createClientComponentClient();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [turnstileMessage, setTurnstileMessage] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
  const isTurnstileConfigured = !!turnstileSiteKey;
  const intent = searchParams.get("intent") === "faceless-video"
    ? "faceless-video"
    : null;
  const requestedNext = searchParams.get("next");
  const safeNext = getSafeAuthReturnPath(requestedNext, "");
  const workflowPreset = facelessWorkflowPresetFromSearchParams(searchParams);
  const rawSource = searchParams.get("source") || "direct";
  const source = /^[a-z0-9-]{1,80}$/i.test(rawSource) ? rawSource : "direct";
  const attribution = normalizeSeoAttribution({
    source,
    landingPath: searchParams.get("landingPath") || "/",
    ctaLocation: searchParams.get("location") || "unknown",
    locale: searchParams.get("locale") || locale,
    intent: intent || undefined,
  });
  const attributionQuery = attribution
    ? `&landingPath=${encodeURIComponent(attribution.landingPath)}&location=${encodeURIComponent(attribution.ctaLocation)}&locale=${encodeURIComponent(attribution.locale)}`
    : "";
  const defaultDestination = intent === "faceless-video"
    ? `${getLocalizedPath("/dashboard/create-workflow/faceless-video", locale)}?intent=faceless-video&source=${encodeURIComponent(source)}${attributionQuery}`
    : getLocalizedPath("/dashboard", locale);
  const destinationBase = safeNext
    || defaultDestination;
  const destinationPath = intent === "faceless-video"
    ? appendFacelessWorkflowPresetToPath(destinationBase, workflowPreset)
    : destinationBase;
  const quickStartCandidate = !safeNext;
  const quickStartDestination = `${getLocalizedPath("/dashboard/quick-start", locale)}?source=${encodeURIComponent(source)}${attributionQuery}`;

  const resolvePostSignupDestination = useCallback(async () => {
    if (!quickStartCandidate) return destinationPath;
    try {
      const response = await fetch(
        `/api/activation/quick-start?locale=${encodeURIComponent(locale)}`,
        { cache: "no-store" },
      );
      const payload = await response.json();
      return response.ok && payload?.enabled && payload?.eligible
        ? quickStartDestination
        : destinationPath;
    } catch {
      return destinationPath;
    }
  }, [destinationPath, locale, quickStartCandidate, quickStartDestination]);

  const getAuthCallbackUrl = () => {
    const callbackUrl = new URL("/api/auth/callback", window.location.origin);
    callbackUrl.searchParams.set("next", destinationPath);
    callbackUrl.searchParams.set("flow", "signup");
    if (quickStartCandidate) {
      callbackUrl.searchParams.set("quick_start_candidate", "1");
    }
    if (intent) callbackUrl.searchParams.set("intent", intent);
    callbackUrl.searchParams.set("source", source);
    if (attribution) {
      callbackUrl.searchParams.set("landingPath", attribution.landingPath);
      callbackUrl.searchParams.set("location", attribution.ctaLocation);
      callbackUrl.searchParams.set("locale", attribution.locale);
    }
    return callbackUrl.toString();
  };

  const persistAttribution = async (
    value: SeoAttributionInput | null,
    signupCompleted: boolean,
  ) => {
    if (!value) return;
    await fetch("/api/seo/attribution", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attribution: value,
        signupCompleted,
      }),
    });
  };

  useEffect(() => {
    trackSeoEvent("signup_started", {
      source,
      intent: intent || "direct",
    });
  }, [intent, source]);

  // 检查用户是否已经登录，如果已登录则跳转到 dashboard
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        router.push(await resolvePostSignupDestination());
      }
    };

    checkUser();
  }, [resolvePostSignupDestination, router, supabase]);

  const resetTurnstile = useCallback(() => {
    setCaptchaToken(null);
    setTurnstileMessage(null);
    setTurnstileResetKey((current) => current + 1);
  }, []);

  const handleCaptchaTokenChange = useCallback((token: string | null) => {
    setCaptchaToken(token);
  }, []);

  const handleTurnstileStatusChange = useCallback(([status, errorCode]: [TurnstileStatus, string?]) => {
    if (status === "ready" || status === "verified") {
      setTurnstileMessage(null);
      return;
    }

    setTurnstileMessage(getTurnstileStatusMessage(status, errorCode, locale));
  }, [locale]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const redirectURL = getAuthCallbackUrl();
      if (password !== confirmPassword) {
        toast.error(t("Passwords do not match"));
        return;
      }

      if (!isTurnstileConfigured) {
        toast.error("Cloudflare Turnstile is not configured");
        return;
      }

      if (!captchaToken) {
        toast.error(turnstileMessage || "Please complete the verification challenge");
        return;
      }

      const normalizedEmail = email.trim().toLowerCase();
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: redirectURL,
          captchaToken,
        },
      });
      if (error) throw error;
      setIsDisabled(true);
      resetTurnstile();

      if (data.session) {
        await persistAttribution(attribution, true);
        trackSeoEvent("signup_completed", {
          source,
          page: attribution?.landingPath,
          location: attribution?.ctaLocation,
          locale: attribution?.locale,
          intent: intent || "direct",
        });
        toast.success(t("Sign in"));
        router.push(await resolvePostSignupDestination());
        return;
      }

      toast.success(t("Check your emails to confirm your account!"));
    } catch (error: any) {
      toast.error(getSignupErrorMessage(error?.message, error?.code, locale));
      resetTurnstile();
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async (e: React.MouseEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (!isTurnstileConfigured) {
        toast.error("Cloudflare Turnstile is not configured");
        return;
      }

      if (!captchaToken) {
        toast.error(turnstileMessage || "Please complete the verification challenge");
        return;
      }

      const redirectURL = getAuthCallbackUrl();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: redirectURL,
          captchaToken,
        } as any,
      } as any);
      if (error) {
        toast.error(getSignupErrorMessage(error.message, (error as any).code, locale));
        resetTurnstile();
      }
    } catch (error: any) {
      toast.error(getSignupErrorMessage(error?.message, error?.code, locale));
      resetTurnstile();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex flex-col items-center justify-center p-8 md:p-24" data-theme={config.colors.theme}>
      <div className="w-full max-w-md space-y-8">

        <div className="text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-2">
            {t("Sign up")} {config.appName}
          </h1>
          <p className="text-base-content/60">
            {t("Already have an account?")} {" "}
            <Link
              href={`${getLocalizedPath("/login", locale)}${
                intent || safeNext
                  ? `?next=${encodeURIComponent(destinationPath)}&source=${encodeURIComponent(source)}`
                    : ""
              }`}
              className="text-primary hover:text-primary-focus"
            >
              {t("Sign in")}
            </Link>
          </p>
        </div>

        <div className="space-y-6">
          <button
            className="btn btn-outline btn-block"
            onClick={handleGoogleSignup}
            disabled={isLoading || isDisabled || !isTurnstileConfigured || !captchaToken}
          >
            {isLoading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              <svg className="w-5 h-5" viewBox="0 0 48 48">
                <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z" />
                <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z" />
                <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z" />
                <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z" />
              </svg>
            )}
            {t("Continue with Google")}
          </button>

          <div className="divider text-xs text-base-content/50 font-medium">
            {t("OR")}
          </div>

          <form
            className="form-control space-y-4"
            onSubmit={handleSignup}
          >
            <div>
              <input
                required
                type="email"
                value={email}
                autoComplete="email"
                placeholder={t("Enter your email")}
                className="input input-bordered w-full placeholder:opacity-60"
                onChange={(e) => setEmail(e.target.value.trimStart())}
              />
            </div>

            <div className="relative">
              <input
                required
                type={showPassword ? "text" : "password"}
                value={password}
                autoComplete="new-password"
                placeholder={t("Enter your password")}
                className="input input-bordered w-full pr-10"
                minLength={6}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>

            <input
              required
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              autoComplete="new-password"
              placeholder={t("Confirm your password")}
              className="input input-bordered w-full"
              minLength={6}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <div className="space-y-2">
              <CloudflareTurnstile
                siteKey={turnstileSiteKey}
                onTokenChange={handleCaptchaTokenChange}
                onStatusChange={handleTurnstileStatusChange}
                resetKey={turnstileResetKey}
              />
              {turnstileMessage && (
                <p className="text-sm text-error">
                  {turnstileMessage}
                </p>
              )}
              {!isTurnstileConfigured && (
                <p className="text-sm text-error">
                  Cloudflare Turnstile is not configured. Set `NEXT_PUBLIC_TURNSTILE_SITE_KEY`.
                </p>
              )}
            </div>

            <button
              className="btn btn-primary btn-block"
              disabled={isLoading || isDisabled || !isTurnstileConfigured || !captchaToken}
              type="submit"
            >
              {isLoading && (
                <span className="loading loading-spinner loading-xs"></span>
              )}
              {t("Create account")}
            </button>
          </form>

          <p className="text-sm text-center text-base-content/60">
            {t("By signing up, you agree to our")} {" "}
            <Link href={getLocalizedPath("/tos", locale)} className="text-primary hover:text-primary-focus">
              {t("Terms")}
            </Link> {t("and")} {" "}
            <Link href={getLocalizedPath("/privacy-policy", locale)} className="text-primary hover:text-primary-focus">
              {t("Privacy Policy")}
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
